/**
 * Soren Panel V2.
 * The persistent daily command center with two peer tabs:
 * 1. Today (daily cash briefing, needs attention, quiet for now)
 * 2. Past answers (durable server-stored answered cards, awaiting confirmation)
 * Host for the search/chat overlay which renders above Today.
 */

import { useState, useRef, useCallback } from 'react'
import type { RankSorenResult, SorenCard } from './rankSorenCards'
import type { CashForecastBriefing, BriefingStatus } from './SorenTodayView'
import SorenTodayView from './SorenTodayView'
import SorenPastAnswersView from './SorenPastAnswersView'
import SorenSearchOverlay from './SorenSearchOverlay'
import SorenCostsForm from './SorenCostsForm'
import SorenAsksFlow from './SorenAsksFlow'
import SorenCashBankAsk from './SorenCashBankAsk'
import type { WeeklyCostPrompt } from '../../api/weeklyCosts'
import SorenFeedbackSheet from './SorenFeedbackSheet'
import {
  useSorenAnswers,
  type PastAnswer,
  type PastAnswerStatus,
} from './sorenAnswers'
import type { AriaChatReply } from '../../api/ariaChat'

interface SorenPanelProps {
  open: boolean
  activeTab: 'today' | 'past'
  searchOverlayOpen: boolean
  searchOverlayAutoRecord: boolean
  searchOverlayInitialType: string | null
  ranked: RankSorenResult
  briefing: CashForecastBriefing | null
  briefingStatus: BriefingStatus
  fetchFailed?: boolean
  isUnauthorized?: boolean
  onRetryCash?: () => void
  onRetryItems?: () => void
  userName?: string | null
  onClosePanel: () => void
  onSwitchTab: (tab: 'today' | 'past') => void
  onOpenSearchOverlay: (type?: string | null, autoRecord?: boolean) => void
  onCloseSearchOverlay: () => void
  onOpenCardHref: (href: string) => void
  onCardDone: (card: SorenCard) => void
  onCardRemind: (card: SorenCard) => void
  onCardHide: (card: SorenCard) => void
  onUpdateBank: () => void
  onOpenCashReport?: () => void
  pageVersionId?: string | null
  subSheet?: 'costs' | 'feedback' | null
  subSheetWeek?: string | null
  subSheetSource?: string | null
  onCloseSubSheet?: () => void
  costPrompt?: WeeklyCostPrompt | null
  onCostPromptDone?: () => void
  onOpenCostsWeek?: (weekStart: string) => void
}

export default function SorenPanel({
  open,
  activeTab,
  searchOverlayOpen,
  searchOverlayAutoRecord,
  searchOverlayInitialType,
  ranked,
  briefing,
  briefingStatus,
  fetchFailed,
  isUnauthorized,
  onRetryCash,
  onRetryItems,
  userName,
  onClosePanel,
  onSwitchTab,
  onOpenSearchOverlay,
  onCloseSearchOverlay,
  onOpenCardHref,
  onCardDone,
  onCardRemind,
  onCardHide,
  onUpdateBank,
  onOpenCashReport,
  pageVersionId = null,
  subSheet = null,
  subSheetWeek = null,
  subSheetSource = null,
  onCloseSubSheet,
  costPrompt = null,
  onCostPromptDone,
  onOpenCostsWeek,
}: SorenPanelProps) {
  const mainContentRef = useRef<HTMLDivElement | null>(null)
  const [historyFilter, setHistoryFilter] = useState<'recent' | 'archive'>('recent')

  const {
    items: answers,
    recentCount,
    hasMore,
    isFetching,
    loadMore,
    createAnswer,
    updateStatus,
    archiveAnswer,
  } = useSorenAnswers({ filter: historyFilter })

  const todayCount = isUnauthorized ? 0 : ranked.doFirst.length + ranked.thenReview.length
  // Past answers badge counts recent only (per requirement 6 & 7)
  let pastCount = recentCount
  if (isUnauthorized) pastCount = 0

  // Generate greeting: e.g. "Afternoon, Vagish."
  const now = new Date()
  const hour = now.getHours()
  const part = hour < 12 ? 'Morning' : hour < 17 ? 'Afternoon' : 'Evening'
  const firstName = (userName || '').trim().split(/\s+/)[0]
  const greetingTitle = isUnauthorized
    ? 'Session Expired'
    : firstName
      ? `${part}, ${firstName}.`
      : `${part}.`
  const monthName = now.toLocaleDateString('en-US', { month: 'short' })
  const dayNum = now.getDate()
  const dateSub = isUnauthorized
    ? 'Sign in required'
    : briefingStatus === 'ready'
      ? `Your cash briefing is ready · ${monthName} ${dayNum}`
      : 'Daily command center'

  function handleOpenSavedAnswer(ans: PastAnswer) {
    onOpenSearchOverlay(ans.type.toLowerCase(), false)
  }

  const handleSavePastAnswer = useCallback(
    (payload: {
      question: string
      oneLineResult: string
      type?: PastAnswer['type']
      replyData?: AriaChatReply | null
      awaitingConfirmation?: boolean
      toolNames?: string[]
      projectId?: number | null
      answerJson?: Record<string, unknown>
    }): PastAnswer => {
      const status: PastAnswerStatus = payload.awaitingConfirmation
        ? 'awaiting_confirmation'
        : 'ready'
      const id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : '00000000-0000-4000-8000-' +
            Math.random().toString(16).slice(2, 14).padEnd(12, '0')

      const optimistic: PastAnswer = {
        id,
        question: payload.question,
        type: payload.type || 'Answer',
        timeLabel: 'Today · Just now',
        oneLineResult: payload.oneLineResult,
        status,
        awaitingConfirmation: Boolean(payload.awaitingConfirmation),
        replyData: payload.replyData || null,
        answerJson:
          payload.answerJson ||
          (payload.replyData ? { reply_data: payload.replyData } : {}),
        toolNames: payload.toolNames || [],
        projectId: payload.projectId ?? null,
        isArchived: false,
        createdAt: Date.now(),
        rawCreatedAt: new Date().toISOString(),
      }

      void createAnswer({
        id,
        question: payload.question,
        question_type: payload.type || 'Answer',
        one_line_result: payload.oneLineResult,
        status,
        tool_names: payload.toolNames || [],
        project_id: payload.projectId ?? null,
        answer_json:
          payload.answerJson ||
          (payload.replyData ? { reply_data: payload.replyData } : {}),
      })

      return optimistic
    },
    [createAnswer]
  )

  const handleUpdateConfirmation = useCallback(
    (id: string, awaitingOrStatus: boolean | PastAnswerStatus) => {
      let status: PastAnswerStatus
      if (typeof awaitingOrStatus === 'boolean') {
        status = awaitingOrStatus ? 'awaiting_confirmation' : 'confirmed'
      } else {
        status = awaitingOrStatus
      }
      void updateStatus({ id, status })
    },
    [updateStatus]
  )

  return (
    <aside
      className={`soren-panel ${!open ? 'closed' : ''}`}
      id="sorenPanel"
      data-testid="soren-sheet"
      aria-label="Soren assistant panel"
      role="dialog"
    >
      <header className="panel-header">
        <span className="s-mark">S</span>
        <div className="s-title">
          <b>{greetingTitle}</b>
          <span>{dateSub}</span>
        </div>
        <span className="spacer" />
        <button
          className="close-btn"
          id="closePanel"
          data-testid="soren-panel-close"
          type="button"
          aria-label="Close Soren"
          onClick={onClosePanel}
        >
          ×
        </button>
      </header>

      <nav className="panel-tabs" data-testid="soren-panel-tabs" aria-label="Soren views">
        <button
          className={`panel-tab ${activeTab === 'today' ? 'active' : ''}`}
          data-testid="soren-tab-today"
          data-tab="today"
          type="button"
          onClick={() => onSwitchTab('today')}
        >
          Today <span className="tab-count">{todayCount}</span>
        </button>
        <button
          className={`panel-tab ${activeTab === 'past' ? 'active' : ''}`}
          data-testid="soren-tab-past"
          data-tab="past"
          type="button"
          onClick={() => onSwitchTab('past')}
        >
          Past answers <span className="tab-count" id="pastCount">{pastCount}</span>
        </button>
      </nav>

      {/* Ask Soren quick launcher row */}
      <div className="ask-launch-wrap">
        <button
          className="ask-launch"
          id="openSearch"
          data-testid="soren-ask-launch"
          type="button"
          onClick={() => onOpenSearchOverlay(null, false)}
        >
          <span className="ask-launch-icon">S</span>
          <span>Ask Soren about cash, margin, Pay Apps, or costs…</span>
          <span className="ask-launch-label">Ask</span>
        </button>
        <button
          className="quick-mic"
          id="quickMic"
          data-testid="soren-quick-mic"
          type="button"
          aria-label="Speak to Soren"
          onClick={() => onOpenSearchOverlay(null, true)}
        >
          <span className="mic-glyph" aria-hidden="true" />
        </button>
      </div>

      <div className="main-content" id="mainContent" ref={mainContentRef}>
        {costPrompt && onCostPromptDone && (
          <>
            <SorenCashBankAsk show={!!costPrompt.show} />
            <SorenAsksFlow prompt={costPrompt} onDone={onCostPromptDone} onOpenCostsWeek={onOpenCostsWeek} />
          </>
        )}
        {subSheet === 'costs' ? (
          <div style={{ padding: '16px 20px' }}>
            <SorenCostsForm weekStart={subSheetWeek} />
            <button type="button" className="btn btn-ghost" style={{ marginTop: 16 }} onClick={onCloseSubSheet}>
              Back
            </button>
          </div>
        ) : subSheet === 'feedback' ? (
          <div style={{ padding: '16px 20px' }}>
            <SorenFeedbackSheet
              source={subSheetSource}
              weekStart={subSheetWeek}
              onClose={() => onCloseSubSheet?.()}
            />
          </div>
        ) : isUnauthorized ? (
          <div
            className="soren-unauthorized-state"
            data-testid="soren-unauthorized-state"
            style={{ padding: '48px 24px', textAlign: 'center' }}
          >
            <div style={{ fontSize: 36, marginBottom: 12 }}>🔒</div>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: 'var(--ink, #111)', marginBottom: 8 }}>
              Session expired — sign in again
            </h2>
            <p style={{ fontSize: 13, color: 'var(--ink3, #666)', marginBottom: 20, lineHeight: 1.5 }}>
              Your session has expired. Sign in to view your cash forecast and active items.
            </p>
            <a
              href="/login"
              className="btn primary"
              data-testid="soren-signin-link"
              style={{ display: 'inline-block', padding: '8px 20px', textDecoration: 'none', fontWeight: 600 }}
            >
              Sign in
            </a>
          </div>
        ) : activeTab === 'today' ? (
          <SorenTodayView
            ranked={ranked}
            briefing={briefing}
            briefingStatus={briefingStatus}
            fetchFailed={fetchFailed}
            onRetryCash={onRetryCash}
            onRetryItems={onRetryItems}
            onOpenCashDetail={() => onOpenSearchOverlay('cash', false)}
            onUpdateBank={onUpdateBank}
            onOpenCardHref={onOpenCardHref}
            onCardDone={onCardDone}
            onCardRemind={onCardRemind}
            onCardHide={onCardHide}
          />
        ) : (
          <SorenPastAnswersView
            answers={answers}
            filter={historyFilter}
            onFilterChange={setHistoryFilter}
            hasMore={hasMore}
            onLoadMore={loadMore}
            isLoading={isFetching}
            onOpenAnswer={handleOpenSavedAnswer}
            onArchiveAnswer={(id) => void archiveAnswer(id)}
          />
        )}
      </div>

      <footer className="main-footer">
        Today stays focused on cash and action. Completed questions live in Past answers.
      </footer>

      {/* Search overlay: renders above Today / Past */}
      <SorenSearchOverlay
        open={searchOverlayOpen}
        autoRecord={searchOverlayAutoRecord}
        initialType={searchOverlayInitialType}
        briefing={briefing}
        onClose={onCloseSearchOverlay}
        onSavePastAnswer={handleSavePastAnswer}
        onUpdateConfirmation={handleUpdateConfirmation}
        pageVersionId={pageVersionId}
        onOpenCashReport={onOpenCashReport}
        onOpenSource={onOpenCardHref}
      />
    </aside>
  )
}
